/**
 * Frontend i18n — gettext-compatible API (per org I18N-STANDARDS.md).
 * Source language: English (msgid values in code are English).
 * Korean translations loaded from the dictionary below.
 *
 * Usage:  import { t } from '@/lib/i18n'
 *         <button>{t('Add project')}</button>
 */

import { getLocale, type Locale } from './locale'

type Catalog = Record<string, string>

// ─── Catalogs ─────────────────────────────────────────────────
// English = identity (msgid IS the text)
const en: Catalog = {}

// Korean translations
const ko: Catalog = {
  // ─── Sidebar / Nav ───
  'Dashboard': '대시보드',
  'Projects': '프로젝트',
  'Ledger': '원장',
  'Monitors': '모니터',
  'Assets': '에셋',
  'Incidents': '인시던트',
  'Settings': '설정',
  'Operations': '운영',

  // ─── Common ───
  'Add project': '프로젝트 추가',
  'Add monitor': '모니터 추가',
  'Add entry': '항목 추가',
  'Add asset': '에셋 추가',
  'Save': '저장',
  'Cancel': '취소',
  'Delete': '삭제',
  'Loading...': '로딩 중...',
  'Retry': '다시 시도',
  'All': '전체',
  'Active': '활성',
  'Idea': '아이디어',

  // ─── Dashboard ───
  'Net burn this month': '이번 달 순소비',
  'Revenue': '수익',
  'Cost': '비용',
  'Net': '순이익',
  'Previous month net': '전월 net',
  'Cost breakdown': '비용 분류',
  'Project P&L': '프로젝트 손익',
  'Recent activity': '최근 활동',
  'All systems operational': '모든 시스템 정상',
  'monitors down': '모니터 다운',
  'Asset expiry': '에셋 만료',
  'within 30 days': '30일 내',
  'Safe': '안전',
  'First month': '첫 달',
  'View all': '전체 보기',
  'View details': '전체 내역',
  'incidents open': '개 인시던트 열려 있음',
  'assets expiring soon': '개 에셋 만료 임박',

  // ─── Empty states ───
  'No data available': '데이터가 없습니다',
  'No projects yet': '아직 프로젝트가 없습니다',
  'Add your first project to get started': '첫 프로젝트를 추가해 운영을 시작하세요',
  'No monitors yet': '모니터가 없습니다',
  'Add a monitor to track service health': '첫 모니터를 추가해 서비스 상태를 추적하세요',
  'No ledger entries': '원장 항목이 없습니다',
  'No financial movements in this period': '이 기간에 기록된 금전 이동이 없습니다',
  'No assets yet': '에셋이 없습니다',
  'Add domains, SSL, API keys to track expiry': '도메인, SSL, API 키를 추가해 만료를 추적하세요',
  'No open incidents': '열린 인시던트가 없습니다',
  'All services operational': '모든 서비스가 정상 작동 중입니다',
  'No recent activity': '최근 활동 없음',

  // ─── Error ───
  'Failed to load data': '데이터를 불러오지 못했습니다',

  // ─── Login ───
  'Operations Console Login': '운영 콘솔 로그인',
  'Service portfolio · ledger · monitoring': '서비스 포트폴리오 · 원장 · 모니터링',
  'Admin password': '관리자 비밀번호',
  'Enter password': '비밀번호 입력',
  'Login': '로그인',
  'Logging in...': '로그인 중...',
  'Incorrect password': '잘못된 비밀번호입니다',

  // ─── Project form ───
  'Name': '이름',
  'Slug (identifier)': 'Slug (식별자)',
  'Description': '설명',
  'Platform (hosting)': '플랫폼 (호스팅 환경)',
  'Type (project nature)': '타입 (프로젝트 성격)',
  'Display name shown in dashboard and project list': '표시용 이름입니다. 대시보드·프로젝트 목록에 표시됩니다.',
  'Unique identifier for URL, API, ingest. Lowercase letters, numbers, hyphens only. Auto-generated from name.': 'URL·API·ingest에서 사용하는 고유 식별자입니다. 소문자 영문+숫자+하이픈만 가능. 이름 입력 시 자동 생성됩니다.',
  'One-line project description. Shown on cards (2 lines max).': '프로젝트를 한 줄로 설명합니다. 카드에 2줄까지 표시됩니다.',
  'Cloudflare — Workers/Pages/D1/R2. Quay can monitor and collect costs directly.': 'Cloudflare — Workers/Pages/D1/R2. Quay가 직접 모니터링·비용 수집 가능',
  'Local — local server (Pi etc). Quay cannot reach it; heartbeat/ingest push only.': 'Local — 로컬 서버 (Pi 등). Quay가 접근 불가 → heartbeat·ingest 푸시만 가능',
  'Group and filter by type in dashboard.': '대시보드에서 타입별로 그룹화·필터링할 수 있습니다.',
  'Adding...': '추가 중...',

  // ─── Project types ───
  'SaaS — software as a service': 'SaaS — 서비스형 소프트웨어 (고객이 사용)',
  'Tool — utility/tool': 'Tool — 도구·유틸리티 (개인/팀 사용)',
  'Internal — internal operations tool': 'Internal — 내부 운영 도구 (Quay 자체 등)',
  'Library — library/package': 'Library — 라이브러리·패키지 (npm, PyPI)',
  'Marketing — landing/promo page': 'Marketing — 홍보·랜딩 페이지',

  // ─── Ledger ───
  'Revenue (this month)': '수익 (이번 달)',
  'Inbound': '수입',
  'Outbound': '지출',
  'Date': '날짜',
  'Direction': '방향',
  'Category': '카테고리',
  'Amount': '금액',
  'inbound': '수익',
  'outbound': '비용',
  '↓ Revenue (incoming)': '↘ 수익 (들어옴)',
  '↑ Cost (outgoing)': '↗ 비용 (나감)',
  'Amount (KRW)': '금액 (원)',
  'Project (optional)': '프로젝트 (선택)',
  'Unassigned': '미할당',

  // ─── Monitor form ───
  'HTTP (URL check)': 'HTTP (URL 체크)',
  'Heartbeat (local service)': 'Heartbeat (로컬 서비스)',
  'SSL (certificate expiry)': 'SSL (인증서 만료)',
  'Domain (domain expiry)': 'Domain (도메인 만료)',
  'Target URL': '대상 URL',

  // ─── Settings ───
  'General': '일반',
  'Notification channels': '알림 채널',
  'Developer': '개발자',
  'Workspace': '워크스페이스',
  'Workspace name': '워크스페이스명',
  'Locale': '로케일',
  'Base currency': '기본 통화',
  'Korean (ko-KR)': '한국어 (ko-KR)',
  'English (en-US)': 'English (en-US)',
  'API tokens': 'API 토큰',
  'Generate token': '토큰 생성',
  'Token name': '토큰 이름',
  'Webhook endpoints': '웹훅 엔드포인트',
  'No tokens issued': '발급된 토큰이 없습니다',
  'Last used': '마지막 사용',
  'Token created — copy it now, shown only once': '토큰이 생성되었습니다 — 지금만 복사할 수 있습니다',
  'Copy to clipboard': '📋 클립보드에 복사',

  // ─── Status labels ───
  'status active': '활성',
  'status idea': '아이디어',
  'status maintenance': '유지보수',
  'status sunset': '종료 예정',
  'status archived': '보관됨',
}

const CATALOGS: Record<Locale, Catalog> = { en, ko }

// ─── gettext API ──────────────────────────────────────────────

/** Translate a string. msgid is English; returns Korean if locale=ko. */
export function t(msgid: string, params?: Record<string, string | number>): string {
  const locale = getLocale()
  const catalog = CATALOGS[locale] ?? en
  const translated = catalog[msgid] ?? msgid
  return interpolate(translated, params)
}

/** Alias */
export const gettext = t

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    key in params ? String(params[key]) : `{${key}}`,
  )
}
